/**
 * Admin Panel - System Health API
 *
 * The endpoint map for the health check endpoints (internal/external services,
 * OAuth, cron jobs). The bodies live in `./schemas/health-check.schemas.ts` and
 * `./schemas/cron-health.schemas.ts`, and are re-exported from here.
 * Part of the NexusApi system for end-to-end type safety.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ApiSuccessSchema } from "../../common/response.schema";
import {
  CronExecutionHistorySchema,
  CronExecutionStatusSchema,
  CronFailureRateSchema,
  CronJobSummarySchema
} from "./schemas/cron-health.schemas";
import {
  HealthCheckResponseSchema,
  OAuthTokenHealthResultSchema,
  OAuthTokenHealthSummarySchema,
  RefreshExternalHealthResponseSchema
} from "./schemas/health-check.schemas";

export * from "./schemas/cron-health.schemas";
export * from "./schemas/health-check.schemas";

// ============================================================
// ZAdminHealth - Endpoint Definitions
// ============================================================

export const ZAdminHealth = {
  /**
   * Get internal services health
   * Checks: Prisma DB, Redis, BullMQ queues, Typesense
   * GET /api/admin/health/internal
   */
  GetInternalHealth: {
    method: "GET" as const,
    path: "/api/admin/health/internal",
    Response: ApiSuccessSchema(HealthCheckResponseSchema)
  },

  /**
   * Get external services health
   * Checks: Clerk, OpenAI, Anthropic, Stripe (via cached rate limits)
   * GET /api/admin/health/external
   */
  GetExternalHealth: {
    method: "GET" as const,
    path: "/api/admin/health/external",
    Response: ApiSuccessSchema(HealthCheckResponseSchema)
  },

  /**
   * Refresh external API rate limits
   * Re-probes and updates the Redis cache, unless an ad-hoc sweep ran inside
   * the shared cooldown — then `probed: false` and the cached snapshot.
   * POST /api/admin/health/external/refresh
   */
  RefreshExternalHealth: {
    method: "POST" as const,
    path: "/api/admin/health/external/refresh",
    Response: ApiSuccessSchema(RefreshExternalHealthResponseSchema)
  },

  /**
   * Get OAuth token health summary
   * Returns connection health without triggering refresh
   * GET /api/admin/health/oauth
   */
  GetOAuthHealth: {
    method: "GET" as const,
    path: "/api/admin/health/oauth",
    Response: ApiSuccessSchema(OAuthTokenHealthSummarySchema)
  },

  /**
   * Manually trigger OAuth token health refresh
   * Proactively refreshes expiring/stale tokens
   * POST /api/admin/health/oauth/refresh
   */
  RefreshOAuthHealth: {
    method: "POST" as const,
    path: "/api/admin/health/oauth/refresh",
    Response: ApiSuccessSchema(OAuthTokenHealthResultSchema)
  },

  /**
   * Get all cron jobs with summaries
   * Returns list of jobs with stats and last execution
   * GET /api/admin/health/cron-jobs
   */
  GetCronJobs: {
    method: "GET" as const,
    path: "/api/admin/health/cron-jobs",
    Response: ApiSuccessSchema(z.array(CronJobSummarySchema))
  },

  /**
   * Get FAILED/(FAILED+SUCCESS) per cron key over a trailing window.
   *
   * A companion to GetCronJobs, not a replacement — GetCronJobs is scoped
   * to CRON_DEFAULTS-registered jobs and its `stats.successRate` reports
   * `0` (not "no data") when a cron has zero conclusive runs in the
   * window. This endpoint covers every cron key with an execution log
   * row in the window, and reports `null` rather than `0` in that case.
   * GET /api/admin/health/cron-failure-rates
   */
  GetCronFailureRates: {
    method: "GET" as const,
    path: "/api/admin/health/cron-failure-rates",
    Params: z.object({
      // Trailing window in hours. Capped at 30 days — `findSince` loads
      // every matching row into memory to count statuses, so an
      // unbounded window on a high-frequency cron is an unbounded read.
      windowHours: z.coerce
        .number()
        .min(1)
        .max(24 * 30)
        .prefault(24)
    }),
    Response: ApiSuccessSchema(z.array(CronFailureRateSchema))
  },

  /**
   * Get detailed execution history for a specific cron job
   * Returns paginated list of executions with filtering
   * GET /api/admin/health/cron-jobs/:jobName/executions
   */
  GetCronExecutions: {
    method: "GET" as const,
    path: "/api/admin/health/cron-jobs/:jobName/executions",
    PathVars: z.object({ jobName: z.nativeEnum(DbEnum.CronKey) }),
    Params: z.object({
      page: z.coerce.number().optional(),
      limit: z.coerce.number().optional(),
      status: CronExecutionStatusSchema.optional(),
      includeSkipped: z
        .union([z.boolean(), z.string()])
        .transform((val) => val === true || val === "true")
        .prefault(false) // Hide SKIPPED by default; prefault feeds the transform (matches v4 migration pattern)
    }),
    Response: ApiSuccessSchema(CronExecutionHistorySchema)
  },

  /**
   * Manually trigger a cron job execution
   * Runs the job immediately outside its schedule
   * POST /api/admin/health/cron-jobs/:jobName/trigger
   */
  TriggerCronJob: {
    method: "POST" as const,
    path: "/api/admin/health/cron-jobs/:jobName/trigger",
    PathVars: z.object({ jobName: z.nativeEnum(DbEnum.CronKey) }),
    Response: ApiSuccessSchema(
      z.object({
        message: z.string(),
        executionId: z.string().optional(),
        metadata: z.record(z.string(), z.unknown()).optional()
      })
    )
  },

  /**
   * Cleanup old cron execution logs
   * Deletes logs older than specified retention period
   * POST /api/admin/health/cron-jobs/cleanup
   */
  CleanupCronLogs: {
    method: "POST" as const,
    path: "/api/admin/health/cron-jobs/cleanup",
    Body: z.object({ daysToKeep: z.number().min(7).max(365) }),
    Response: ApiSuccessSchema(z.object({ deleted: z.number() }))
  }
} as const;
